'use client';

import React, { ReactNode } from 'react';

import { Title, TitleLevels, TitleMarkup, TypographyAlign } from '@bytel/trilogy-react';

import { FaqSlugType, partials } from '@helpers/partials';

import { SSIBlock } from './ssi-block';

const getTitleLevelFromMarkup = (markup: TitleMarkup): TitleLevels => {
    switch (markup) {
        case TitleMarkup.H1:
            return TitleLevels.ONE;
        case TitleMarkup.H2:
            return TitleLevels.TWO;
        case TitleMarkup.H3:
            return TitleLevels.THREE;
        case TitleMarkup.H4:
            return TitleLevels.FOUR;
        case TitleMarkup.H5:
            return TitleLevels.FIVE;
        case TitleMarkup.H6:
            return TitleLevels.SIX;
        default:
            return TitleLevels.TWO;
    }
};

type FaqProps = {
    id: string;
    url: FaqSlugType;
    title: string | ReactNode;
    titleAlt?: string;
    titleMarkup?: TitleMarkup;
    titleLevel?: TitleLevels;
};

type FaqContainerProps = {
    title: string | ReactNode;
    titleAlt?: string;
    children: ReactNode;
    titleMarkup?: TitleMarkup;
    titleLevel?: TitleLevels;
};

function FaqContainer({
    title,
    titleAlt,
    children,
    titleMarkup = TitleMarkup.H2,
    titleLevel = getTitleLevelFromMarkup(titleMarkup),
}: Readonly<FaqContainerProps>) {
    return (
        <>
            <Title
                markup={titleMarkup}
                level={titleLevel}
                typo={TypographyAlign.TEXT_CENTERED}
                accessibilityLabel={titleAlt}
            >
                {title}
            </Title>
            {children}
        </>
    );
}

export function Faq({ id, url, title, titleAlt, titleMarkup, titleLevel }: Readonly<FaqProps>) {
    return (
        <SSIBlock
            tagId={id}
            url={partials.FAQ(url)}
            container={FaqContainer}
            containerProps={{ title, titleAlt, titleMarkup, titleLevel }}
        />
    );
}
